import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import {
  textLoads,
  type TextLoad,
  type TextLoadCheck,
  type TextLoadInput,
  type TextLoadItem,
  type TextLoadKind,
  type TextLoadResult,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { formatDateTime } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Card, CardHeader } from '@/shared/ui/Card';
import { DataTable, type Column } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Field } from '@/shared/ui/Field';
import { Modal } from '@/shared/ui/Modal';
import { useToast } from '@/shared/ui/useToast';

import { itensRecusados } from './itensRecusados';
import { lerPacote } from './lerPacote';

/**
 * Aba "Carga de textos" de /templates (P-MIN-5): o pacote de textos da seguradora
 * (objeto e condicoes da modalidade, clausulas particulares, nome da cobertura
 * impresso e modelos de e-mail), conferido contra o vocabulario e gravado tudo ou nada.
 * Ver docs/ADR/0064-carga-dos-textos-e-minutas-da-seguradora.md.
 *
 * Os textos da seguradora nao ficam no repositorio: sao carregados por empresa. O
 * texto da modalidade vira rascunho, que outra pessoa ativa na modalidade; o modelo de
 * e-mail publica a versao seguinte.
 */

export const PERMISSAO_VER_CARGAS = 'templates.carga.view';
const PERMISSAO_CARREGAR = 'templates.carga.create';

const rotuloDoTipo: Record<TextLoadKind, string> = {
  nome_cobertura: 'Nome da cobertura',
  objeto: 'Objeto',
  condicao: 'Condicao',
  clausula_particular: 'Clausula particular',
  email: 'Modelo de e-mail',
};

const rotuloDoResultado: Record<TextLoadResult, string> = {
  novo: 'Novo',
  alterado: 'Alterado',
  igual: 'Igual, nao grava',
  erro: 'Erro',
};

const tomDoResultado: Record<
  TextLoadResult,
  'success' | 'info' | 'neutral' | 'danger'
> = {
  novo: 'success',
  alterado: 'info',
  igual: 'neutral',
  erro: 'danger',
};

/** Le o arquivo como texto. FileReader vale em todo navegador e no ambiente de teste. */
function lerTexto(arquivo: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const leitor = new FileReader();
    leitor.onload = () =>
      resolve(typeof leitor.result === 'string' ? leitor.result : '');
    leitor.onerror = () => reject(new Error('Nao foi possivel ler o arquivo.'));
    leitor.readAsText(arquivo);
  });
}

const colunasDosItens: Column<TextLoadItem>[] = [
  {
    key: 'posicao',
    header: 'Item',
    align: 'right',
    render: (item) => <span className="tabular-nums">{item.position}</span>,
  },
  {
    key: 'tipo',
    header: 'Tipo',
    render: (item) => rotuloDoTipo[item.kind] ?? item.kind,
  },
  {
    key: 'alvo',
    header: 'Alvo',
    render: (item) => (
      <div className="min-w-0">
        <p className="text-ink-900">{item.target || '--'}</p>
        {item.messages.map((mensagem) => (
          <p key={mensagem} className="text-2xs text-danger-700">
            {mensagem}
          </p>
        ))}
      </div>
    ),
  },
  {
    key: 'resultado',
    header: 'Resultado',
    render: (item) => (
      <div className="flex flex-col gap-1">
        <Badge tone={tomDoResultado[item.result]}>
          {rotuloDoResultado[item.result]}
        </Badge>
        {item.version ? (
          <span className="text-2xs text-ink-500">Versao {item.version}</span>
        ) : null}
      </div>
    ),
  },
];

export function TextLoadsTab() {
  const { can } = useAuth();
  const [nova, setNova] = useState(false);
  const [vendo, setVendo] = useState<string | null>(null);
  const podeCarregar = can(PERMISSAO_CARREGAR);

  const cargas = useQuery({
    queryKey: ['text-loads'],
    queryFn: ({ signal }) => textLoads.list({ limit: 50 }, signal),
  });

  const colunas: Column<TextLoad>[] = [
    {
      key: 'numero',
      header: 'Carga',
      render: (carga) => (
        <div className="min-w-0">
          <p className="font-medium text-ink-900">Carga {carga.number}</p>
          <p className="truncate text-2xs text-ink-500">
            {carga.label || 'Sem descricao'}
          </p>
        </div>
      ),
    },
    {
      key: 'itens',
      header: 'Itens',
      render: (carga) =>
        `${carga.item_count} (novos ${carga.created_count}, alterados ${carga.changed_count}, iguais ${carga.unchanged_count})`,
    },
    {
      key: 'quem',
      header: 'Quem carregou',
      hideBelow: 'md',
      render: (carga) => (
        <div className="text-2xs text-ink-700">
          <p>{carga.created_label || '--'}</p>
          <p className="text-ink-500">{formatDateTime(carga.created_at)}</p>
        </div>
      ),
    },
    {
      key: 'acoes',
      header: 'Acoes',
      align: 'right',
      render: (carga) => (
        <Button size="sm" variant="secondary" onClick={() => setVendo(carga.id)}>
          Ver itens
        </Button>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-5">
      <Alert tone="info" title="Como a carga funciona">
        O pacote e conferido inteiro antes de gravar: com qualquer item recusado, nada e
        gravado. O objeto e as condicoes da modalidade viram rascunho, que outra pessoa
        ativa na modalidade; a clausula de mesmo nome sobe de versao; o modelo de e-mail
        publica a versao seguinte. O item igual ao que ja esta gravado nao grava de
        novo.
      </Alert>

      {cargas.isError ? (
        <Alert tone="danger" title="Nao foi possivel carregar as cargas">
          {cargas.error.message}
        </Alert>
      ) : null}

      <Card>
        <CardHeader
          title="Cargas de textos da seguradora"
          action={
            podeCarregar ? (
              <Button onClick={() => setNova(true)}>Nova carga</Button>
            ) : null
          }
        />
        <DataTable
          columns={colunas}
          items={cargas.data?.items ?? []}
          keyOf={(carga) => carga.id}
          loading={cargas.isPending}
          empty={
            <EmptyState
              icon="copy"
              title="Nenhuma carga de textos"
              description="Os textos da seguradora entram por pacote, conferido contra o vocabulario antes de gravar."
            />
          }
        />
      </Card>

      {nova ? <NewLoadDialog onClose={() => setNova(false)} /> : null}
      {vendo ? <LoadDetailDialog id={vendo} onClose={() => setVendo(null)} /> : null}
    </div>
  );
}

function NewLoadDialog({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [pacote, setPacote] = useState<TextLoadInput | null>(null);
  const [descricao, setDescricao] = useState('');
  const [erroDoArquivo, setErroDoArquivo] = useState<string | null>(null);
  const [conferencia, setConferencia] = useState<TextLoadCheck | null>(null);

  const conferir = useMutation({
    mutationFn: (entrada: TextLoadInput) => textLoads.check(entrada),
    onSuccess: (resultado) => {
      setConferencia(resultado);
      carregar.reset();
    },
  });
  const carregar = useMutation({
    mutationFn: (entrada: TextLoadInput) => textLoads.apply(entrada),
    onSuccess: (carga) => {
      toast.success(`Carga ${carga.number} gravada.`);
      void queryClient.invalidateQueries({ queryKey: ['text-loads'] });
      void queryClient.invalidateQueries({ queryKey: ['templates'] });
      onClose();
    },
  });

  async function escolher(arquivo: File) {
    setConferencia(null);
    setErroDoArquivo(null);
    try {
      const lido = lerPacote(await lerTexto(arquivo));
      setPacote(lido);
      setDescricao(lido.label ?? '');
    } catch (erro) {
      setPacote(null);
      setErroDoArquivo(erro instanceof Error ? erro.message : 'Arquivo invalido.');
    }
  }

  const entrada = pacote ? { ...pacote, label: descricao } : null;
  const recusados = itensRecusados(carregar.error);

  return (
    <Modal
      open
      onClose={onClose}
      title="Nova carga de textos"
      description="Escolha o arquivo do pacote (JSON), confira e carregue."
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Voltar
          </Button>
          <Button
            variant="secondary"
            loading={conferir.isPending}
            disabled={entrada === null}
            onClick={() => entrada && conferir.mutate(entrada)}
          >
            Conferir
          </Button>
          <Button
            loading={carregar.isPending}
            disabled={entrada === null || conferencia === null || !conferencia.valid}
            onClick={() => entrada && carregar.mutate(entrada)}
          >
            Carregar
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <label className="flex flex-col gap-1.5 text-xs font-semibold text-ink-700">
          Arquivo do pacote
          <input
            type="file"
            accept="application/json,.json"
            aria-label="Arquivo do pacote"
            onChange={(event) => {
              const arquivo = event.target.files?.[0];
              if (arquivo) void escolher(arquivo);
            }}
            className="text-sm font-normal text-ink-700"
          />
        </label>
        {erroDoArquivo ? (
          <Alert tone="danger" title="Arquivo recusado">
            {erroDoArquivo}
          </Alert>
        ) : null}
        {pacote ? (
          <Field
            label="Descricao da carga"
            value={descricao}
            maxLength={200}
            help={false}
            hint={`${pacote.items.length} itens no pacote.`}
            onChange={(event) => {
              setDescricao(event.target.value);
            }}
          />
        ) : null}
        {conferir.error ? (
          <Alert tone="danger" title="Nao foi possivel conferir">
            {conferir.error.message}
          </Alert>
        ) : null}
        {carregar.error ? (
          <Alert tone="danger" title="A carga foi recusada">
            {carregar.error.message}
          </Alert>
        ) : null}
        {recusados.length > 0 ? (
          <DataTable
            columns={colunasDosItens}
            items={recusados}
            keyOf={(item) => `recusado-${item.position}`}
          />
        ) : null}
        {conferencia ? (
          <>
            <Alert
              tone={conferencia.valid ? 'success' : 'danger'}
              title={
                conferencia.valid
                  ? 'O pacote passou na conferencia'
                  : 'O pacote tem itens com erro'
              }
            >
              {conferencia.valid
                ? 'Nada foi gravado ainda. Carregue para gravar.'
                : 'Corrija os itens com erro no arquivo e confira de novo. Nada foi gravado.'}
            </Alert>
            <DataTable
              columns={colunasDosItens}
              items={conferencia.items}
              keyOf={(item) => String(item.position)}
            />
          </>
        ) : null}
      </div>
    </Modal>
  );
}

function LoadDetailDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const consulta = useQuery({
    queryKey: ['text-loads', id],
    queryFn: ({ signal }) => textLoads.get(id, signal),
  });
  const carga = consulta.data;
  return (
    <Modal
      open
      onClose={onClose}
      title={carga ? `Carga ${carga.number}` : 'Carga de textos'}
      description={
        carga
          ? `${carga.created_label} em ${formatDateTime(carga.created_at)}`
          : undefined
      }
      size="lg"
      footer={
        <Button variant="secondary" onClick={onClose}>
          Fechar
        </Button>
      }
    >
      {consulta.isError ? (
        <Alert tone="danger" title="Nao foi possivel ler a carga">
          {consulta.error.message}
        </Alert>
      ) : (
        <DataTable
          columns={colunasDosItens}
          items={carga?.items ?? []}
          keyOf={(item) => String(item.position)}
          loading={consulta.isPending}
        />
      )}
    </Modal>
  );
}
